'use client';

import React from 'react';
import { useTranslation } from 'react-i18next';
import Link from 'next/link';
import { buttonVariants } from '@/components/ui/Button';
import { Lock, Shield, Scale, CreditCard, Eye, Database, Check, ArrowRight } from 'lucide-react';
import { useIntersectionObserver } from '@/app/hooks/useIntersectionObserver';

export default function TrustContent() {
  const { t } = useTranslation();
  const { ref: heroRef, isVisible: heroVisible } = useIntersectionObserver();
  const { ref: featuresRef, isVisible: featuresVisible } = useIntersectionObserver();
  const { ref: commitmentRef, isVisible: commitmentVisible } = useIntersectionObserver();
  const { ref: ctaRef, isVisible: ctaVisible } = useIntersectionObserver();

  const features = [
    { key: 'encryption', icon: <Lock className="h-10 w-10 text-primary-500" /> },
    { key: 'access', icon: <Shield className="h-10 w-10 text-primary-500" /> },
    { key: 'gdpr', icon: <Scale className="h-10 w-10 text-primary-500" /> },
    { key: 'payments', icon: <CreditCard className="h-10 w-10 text-primary-500" /> },
    { key: 'monitoring', icon: <Eye className="h-10 w-10 text-primary-500" /> },
    { key: 'retention', icon: <Database className="h-10 w-10 text-primary-500" /> },
  ];

  const commitmentsRaw = t('pageContent.trust.commitment.items', { returnObjects: true });
  const commitments: string[] = Array.isArray(commitmentsRaw) ? (commitmentsRaw as string[]) : [];

  return (
    <>
      {/* Hero Section */}
      <section className="py-20 bg-gray-50">
        <div className="container">
          <div
            ref={heroRef}
            className={`max-w-3xl mx-auto text-center fade-in-section ${heroVisible ? 'is-visible' : ''}`}
          >
            <span className="inline-block bg-primary-50 text-primary-600 px-4 py-2 rounded-full text-sm font-semibold mb-4">
              {t('pageContent.trust.hero.tagline')}
            </span>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              {t('pageContent.trust.hero.title')}
            </h1>
            <p className="text-xl text-gray-600 mb-12">
              {t('pageContent.trust.hero.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Security Features */}
      <section className="py-20">
        <div className="container">
          <div
            ref={featuresRef}
            className={`text-center mb-12 fade-in-section ${featuresVisible ? 'is-visible' : ''}`}
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              {t('pageContent.trust.features.title')}
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div
                key={feature.key}
                className={`bg-white rounded-xl shadow-sm border border-gray-100 p-8 hover:shadow-md transition-shadow hover-lift fade-in-section ${featuresVisible ? 'is-visible' : ''}`}
                style={{ transitionDelay: `${index * 0.1}s` }}
              >
                <div className="mb-4">{feature.icon}</div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {t(`pageContent.trust.features.${feature.key}.title`)}
                </h3>
                <p className="text-gray-600">
                  {t(`pageContent.trust.features.${feature.key}.description`)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Commitment */}
      <section className="py-20 bg-gray-50">
        <div className="container">
          <div
            ref={commitmentRef}
            className={`max-w-3xl mx-auto fade-in-section ${commitmentVisible ? 'is-visible' : ''}`}
          >
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                {t('pageContent.trust.commitment.title')}
              </h2>
            </div>
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 md:p-10">
              <ul className="space-y-4">
                {commitments.map((commitment, index) => (
                  <li
                    key={index}
                    className={`flex items-start fade-in-section ${commitmentVisible ? 'is-visible' : ''}`}
                    style={{ transitionDelay: `${index * 0.1}s` }}
                  >
                    <Check className="h-6 w-6 text-primary-500 flex-shrink-0 mr-3 mt-0.5" />
                    <span className="text-gray-700">{commitment}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-primary-500 to-primary-600 text-white">
        <div className="container">
          <div
            ref={ctaRef}
            className={`max-w-3xl mx-auto text-center fade-in-section ${ctaVisible ? 'is-visible' : ''}`}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              {t('pageContent.trust.cta.title')}
            </h2>
            <p className="text-xl text-primary-100 mb-8">
              {t('pageContent.trust.cta.subtitle')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                href="/privacy"
                className={buttonVariants({ variant: 'secondary', size: 'lg', className: 'px-8 hover-scale' })}
              >
                {t('pageContent.trust.cta.privacyButton')}
              </Link>
              <Link
                href="/contact"
                className={buttonVariants({ variant: 'outline', size: 'lg', className: 'px-8 hover-scale border-white text-white hover:bg-white/10' })}
              >
                {t('pageContent.trust.cta.contactButton')}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
